import React, { useEffect, useMemo, useRef, useState } from 'react';
import dataSourcesApi from '@facility/dataSources/api/dataSourcesApi';
import { displayApiError } from '@shared/api/apiClient';
import type { ExternalSourceCandidate } from './types';
import {
    ATTRIBUTE_DATA_TYPE,
    EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE,
    EXTERNAL_ATTRIBUTE_DRIFT_STATUS,
    toAttributeDataType,
} from './types';

export interface LinkedExternalSourceGroup {
    link: IExternalDataSourceLink;
    source?: ExternalSourceCandidate;
    definitions: IExternalAttributeDefinition[];
    draftOnly: boolean;
}

interface LinkedExternalSourceListProps {
    assetClassID: string;
    groups: LinkedExternalSourceGroup[];
    allDefinitions: IExternalAttributeDefinition[];
    matchTargetAttributes: IAttributeDefinition[];
    expandedLinkId: string | null;
    duplicateDisplayNameLinkIds: Set<string>;
    onToggleExpanded: (linkId: string) => void;
    onRemove: (linkId: string) => void;
    onLinkChange: (link: IExternalDataSourceLink) => void;
    onSourceDefinitionsChange: (linkId: string, definitions: IExternalAttributeDefinition[]) => void;
}

interface SourceRowProps {
    assetClassID: string;
    group: LinkedExternalSourceGroup;
    allDefinitions: IExternalAttributeDefinition[];
    matchTargetAttributes: IAttributeDefinition[];
    expanded: boolean;
    duplicateDisplayName: boolean;
    onToggleExpanded: (linkId: string) => void;
    onRemove: (linkId: string) => void;
    onLinkChange: (link: IExternalDataSourceLink) => void;
    onSourceDefinitionsChange: (linkId: string, definitions: IExternalAttributeDefinition[]) => void;
}

const getRowKey = (definition: IExternalAttributeDefinition, index: number) =>
    definition.ID || `${definition.ExternalDataSourceLinkID}:${definition.SourceColumnName}:${index}`;

const getDataTypeLabel = (dataType: AttributeDataType): string => {
    switch (dataType) {
        case ATTRIBUTE_DATA_TYPE.Date:
            return InovxLoader.t('facility:form.dataTypeDate');
        case ATTRIBUTE_DATA_TYPE.Boolean:
            return InovxLoader.t('facility:form.dataTypeBoolean');
        case ATTRIBUTE_DATA_TYPE.Number:
            return InovxLoader.t('facility:form.dataTypeNumber');
        case ATTRIBUTE_DATA_TYPE.Link:
            return InovxLoader.t('facility:form.dataTypeLink');
        case ATTRIBUTE_DATA_TYPE.String:
        default:
            return InovxLoader.t('facility:form.dataTypeString');
    }
};

const getDriftLabel = (status: ExternalAttributeDriftStatus): string => {
    switch (status) {
        case EXTERNAL_ATTRIBUTE_DRIFT_STATUS.MissingColumn:
            return InovxLoader.t('facility:externalAttributes.driftMissingColumn');
        case EXTERNAL_ATTRIBUTE_DRIFT_STATUS.TypeChanged:
            return InovxLoader.t('facility:externalAttributes.driftTypeChanged');
        case EXTERNAL_ATTRIBUTE_DRIFT_STATUS.None:
        default:
            return InovxLoader.t('facility:externalAttributes.driftNone');
    }
};

const getMatchValue = (definition: IExternalAttributeDefinition) => {
    if (!definition.IsForeignKey || !definition.AssetMatchTargetType) {
        return '';
    }

    if (definition.AssetMatchTargetType === EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE.AssetName) {
        return 'assetName';
    }

    return definition.AssetMatchAttributeDefinitionID
        ? `attribute:${definition.AssetMatchAttributeDefinitionID}`
        : '';
};

const getMatchTargetDataType = (dataType: AttributeDataType): AttributeDataType =>
    dataType === ATTRIBUTE_DATA_TYPE.Link
        ? ATTRIBUTE_DATA_TYPE.String as AttributeDataType
        : dataType;

const hasValidMatchTarget = (
    definition: IExternalAttributeDefinition,
    matchTargetAttributes: IAttributeDefinition[]
): boolean => {
    if (!definition.IsForeignKey) {
        return true;
    }

    const matchTargetDataType = getMatchTargetDataType(definition.DataType);
    if (definition.AssetMatchTargetType === EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE.AssetName) {
        return matchTargetDataType === ATTRIBUTE_DATA_TYPE.String;
    }

    return definition.AssetMatchTargetType === EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE.AssetAttribute
        && !!definition.AssetMatchAttributeDefinitionID
        && matchTargetAttributes.some((attribute) =>
            attribute.ID === definition.AssetMatchAttributeDefinitionID
            && attribute.DataType === matchTargetDataType);
};

const buildDefaultMatch = (
    definition: IExternalAttributeDefinition,
    matchTargetAttributes: IAttributeDefinition[]
): Partial<IExternalAttributeDefinition> => {
    const matchTargetDataType = getMatchTargetDataType(definition.DataType);
    if (matchTargetDataType === ATTRIBUTE_DATA_TYPE.String) {
        return {
            AssetMatchTargetType: EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE.AssetName as ExternalAttributeAssetMatchTargetType,
            AssetMatchAttributeDefinitionID: null,
        };
    }

    const attributeTarget = matchTargetAttributes.find((attribute) => attribute.DataType === matchTargetDataType);
    if (attributeTarget) {
        return {
            AssetMatchTargetType: EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE.AssetAttribute as ExternalAttributeAssetMatchTargetType,
            AssetMatchAttributeDefinitionID: attributeTarget.ID,
        };
    }

    return {
        AssetMatchTargetType: null,
        AssetMatchAttributeDefinitionID: null,
    };
};

const applyMatchTargetValue = (
    definition: IExternalAttributeDefinition,
    value: string
): IExternalAttributeDefinition => {
    if (value === 'assetName') {
        return {
            ...definition,
            AssetMatchTargetType: EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE.AssetName as ExternalAttributeAssetMatchTargetType,
            AssetMatchAttributeDefinitionID: null,
        };
    }

    return {
        ...definition,
        AssetMatchTargetType: EXTERNAL_ATTRIBUTE_ASSET_MATCH_TARGET_TYPE.AssetAttribute as ExternalAttributeAssetMatchTargetType,
        AssetMatchAttributeDefinitionID: value.replace('attribute:', ''),
    };
};

const getDuplicateIds = (
    definitions: IExternalAttributeDefinition[],
    getKey: (definition: IExternalAttributeDefinition) => string
) => {
    const counts = new Map<string, number>();
    definitions.forEach((definition) => {
        const key = getKey(definition);
        if (!key) {
            return;
        }

        counts.set(key, (counts.get(key) || 0) + 1);
    });

    return new Set(definitions
        .filter((definition) => {
            const key = getKey(definition);
            return !!key && (counts.get(key) || 0) > 1;
        })
        .map((definition, index) => getRowKey(definition, index)));
};

const LinkedExternalSourceList: React.FC<LinkedExternalSourceListProps> = ({
    assetClassID,
    groups,
    allDefinitions,
    matchTargetAttributes,
    expandedLinkId,
    duplicateDisplayNameLinkIds,
    onToggleExpanded,
    onRemove,
    onLinkChange,
    onSourceDefinitionsChange,
}) => {
    if (!groups.length) {
        return (
            <div className="configuration-form-empty-state external-attributes-empty-state">
                {InovxLoader.t('facility:externalAttributes.emptyLinkedSources')}
            </div>
        );
    }

    return (
        <div className="external-attributes-linked-list">
            {groups.map(group => (
                <SourceRow
                    key={group.link.ID}
                    group={group}
                    assetClassID={assetClassID}
                    allDefinitions={allDefinitions}
                    matchTargetAttributes={matchTargetAttributes}
                    expanded={expandedLinkId === group.link.ID}
                    duplicateDisplayName={duplicateDisplayNameLinkIds.has(group.link.ID)}
                    onToggleExpanded={onToggleExpanded}
                    onRemove={onRemove}
                    onLinkChange={onLinkChange}
                    onSourceDefinitionsChange={onSourceDefinitionsChange}
                />
            ))}
        </div>
    );
};

const SourceRow: React.FC<SourceRowProps> = ({
    assetClassID,
    group,
    allDefinitions,
    matchTargetAttributes,
    expanded,
    duplicateDisplayName,
    onToggleExpanded,
    onRemove,
    onLinkChange,
    onSourceDefinitionsChange,
}) => {
    const link = group.link;
    const source = group.source;
    const definitionByColumnName = useMemo(() => new Map(group.definitions.map((definition) => [definition.SourceColumnName, definition])), [group.definitions]);
    const sourceColumnNames = useMemo(() => new Set((source?.Columns || []).map((column) => column.Name)), [source?.Columns]);
    const columnRows = useMemo(() => {
        const sourceRows = (source?.Columns || []).map((column) => ({
            key: column.Name,
            sourceColumnName: column.Name,
            dataType: toAttributeDataType(column.DataType),
            definition: definitionByColumnName.get(column.Name),
            missingColumn: false,
        }));
        const missingRows = group.definitions
            .filter((definition) => !sourceColumnNames.has(definition.SourceColumnName))
            .map((definition) => ({
                key: definition.SourceColumnName,
                sourceColumnName: definition.SourceColumnName,
                dataType: definition.DataType,
                definition,
                missingColumn: true,
            }));

        return [...sourceRows, ...missingRows];
    }, [definitionByColumnName, group.definitions, source?.Columns, sourceColumnNames]);
    const mappableRows = useMemo(() => columnRows.filter((row) => !row.missingColumn), [columnRows]);
    const mappedMappableRowCount = mappableRows.filter((row) => !!row.definition).length;
    const allMappableRowsMapped = mappableRows.length > 0 && mappedMappableRowCount === mappableRows.length;
    const someMappableRowsMapped = mappedMappableRowCount > 0 && !allMappableRowsMapped;
    const mappedHeaderCheckboxRef = useRef<HTMLInputElement | null>(null);
    const duplicateNameIds = useMemo(
        () => getDuplicateIds(allDefinitions, (definition) => {
            const name = definition.Name.trim().toLocaleLowerCase();
            return name ? `${definition.ExternalDataSourceLinkID}:${name}` : '';
        }),
        [allDefinitions]
    );
    const duplicateMappingIds = useMemo(
        () => getDuplicateIds(allDefinitions, (definition) => `${definition.ExternalDataSourceLinkID}:${definition.SourceColumnName.toLocaleLowerCase()}`),
        [allDefinitions]
    );
    const foreignKeyMappings = useMemo(
        () => group.definitions
            .filter((definition) => definition.IsForeignKey)
            .map((definition) => ({
                SourceColumnName: definition.SourceColumnName,
                DataType: definition.DataType,
                AssetMatchTargetType: definition.AssetMatchTargetType as number,
                AssetMatchAttributeDefinitionID: definition.AssetMatchAttributeDefinitionID,
            })),
        [group.definitions]
    );
    const mappingSignature = JSON.stringify(foreignKeyMappings);
    const mappingSignatureRef = useRef(mappingSignature);
    mappingSignatureRef.current = mappingSignature;
    const countRequestSequence = useRef(0);
    const [mappingCountResult, setMappingCountResult] = useState<{ Count: number; Signature: string } | null>(null);
    const [mappingCountLoading, setMappingCountLoading] = useState(false);

    const changeDefinitions = (definitions: IExternalAttributeDefinition[]) => {
        onSourceDefinitionsChange(link.ID, definitions);
    };

    const createDefinition = (sourceColumnName: string, dataType: AttributeDataType): IExternalAttributeDefinition => ({
        ID: `temp_external_${Date.now()}_${Math.floor(Math.random() * 10000)}_${sourceColumnName}`,
        Name: sourceColumnName,
        DataType: dataType,
        ExternalDataSourceLinkID: link.ID,
        SourceColumnName: sourceColumnName,
        IsForeignKey: false,
        AssetMatchTargetType: null,
        AssetMatchAttributeDefinitionID: null,
        DriftStatus: EXTERNAL_ATTRIBUTE_DRIFT_STATUS.None as ExternalAttributeDriftStatus,
    });

    const toggleColumnMapped = (sourceColumnName: string, dataType: AttributeDataType, mapped: boolean) => {
        if (mapped) {
            if (!definitionByColumnName.has(sourceColumnName)) {
                changeDefinitions([...group.definitions, createDefinition(sourceColumnName, dataType)]);
            }
            return;
        }

        changeDefinitions(group.definitions.filter((definition) => definition.SourceColumnName !== sourceColumnName));
    };

    const toggleAllColumnsMapped = () => {
        if (allMappableRowsMapped) {
            const sourceColumnNamesToUnmap = new Set(mappableRows.map((row) => row.sourceColumnName));
            changeDefinitions(group.definitions.filter((definition) => !sourceColumnNamesToUnmap.has(definition.SourceColumnName)));
            return;
        }

        const nextDefinitions = [...group.definitions];
        mappableRows.forEach((row) => {
            if (!row.definition) {
                nextDefinitions.push(createDefinition(row.sourceColumnName, row.dataType));
            }
        });

        changeDefinitions(nextDefinitions);
    };

    const updateDefinition = (definition: IExternalAttributeDefinition, changes: Partial<IExternalAttributeDefinition>) => {
        changeDefinitions(group.definitions.map((item) => item === definition ? { ...item, ...changes } : item));
    };

    const sourceTitle = link.DisplayName || source?.Name || InovxLoader.t('facility:externalAttributes.unknownSource');
    const sourceSubtitle = source
        ? `${source.ProviderName} - ${source.SourceObjectName}`
        : link.DataSourceID;
    const hasMissingDisplayName = !link.DisplayName.trim();
    const hasDisplayNameError = duplicateDisplayName || hasMissingDisplayName;
    const hasMissingNames = group.definitions.some((definition) => !definition.Name.trim());
    const hasDuplicateNames = group.definitions.some((definition, index) => duplicateNameIds.has(getRowKey(definition, index)));
    const hasDuplicateMappings = group.definitions.some((definition, index) => duplicateMappingIds.has(getRowKey(definition, index)));
    const hasMissingMatchTargets = group.definitions.some((definition) => !hasValidMatchTarget(definition, matchTargetAttributes));
    const hasDrift = group.definitions.some((definition) => definition.DriftStatus !== EXTERNAL_ATTRIBUTE_DRIFT_STATUS.None);
    const hasValidationIssue = hasDisplayNameError
        || hasMissingNames
        || hasDuplicateNames
        || hasDuplicateMappings
        || hasMissingMatchTargets;
    const canCountMappedAssetIdentities = source?.FdwStatus === 1
        && foreignKeyMappings.length > 0
        && !hasDrift
        && group.definitions
            .filter((definition) => definition.IsForeignKey)
            .every((definition) => hasValidMatchTarget(definition, matchTargetAttributes));

    useEffect(() => {
        countRequestSequence.current += 1;
        setMappingCountResult(null);
        setMappingCountLoading(false);
    }, [assetClassID, link.DataSourceID, link.FacilityID, mappingSignature]);

    const countMappedAssetIdentities = async () => {
        const requestSignature = mappingSignature;
        const requestSequence = ++countRequestSequence.current;
        setMappingCountResult(null);
        setMappingCountLoading(true);

        try {
            const result = await dataSourcesApi.countMappedAssetIdentities({
                FacilityID: link.FacilityID,
                DataSourceID: link.DataSourceID,
                AssetClassID: assetClassID,
                Mappings: foreignKeyMappings,
            });
            if (requestSequence === countRequestSequence.current && requestSignature === mappingSignatureRef.current) {
                setMappingCountResult({ Count: result.Count, Signature: requestSignature });
            }
        } catch (error) {
            if (requestSequence === countRequestSequence.current && requestSignature === mappingSignatureRef.current) {
                displayApiError(error);
            }
        } finally {
            if (requestSequence === countRequestSequence.current && requestSignature === mappingSignatureRef.current) {
                setMappingCountLoading(false);
            }
        }
    };

    useEffect(() => {
        const normalizedDefinitions = group.definitions.map((definition) => {
            if (!definition.IsForeignKey || getMatchValue(definition)) {
                return definition;
            }

            const defaultMatch = buildDefaultMatch(definition, matchTargetAttributes);
            if (!defaultMatch.AssetMatchTargetType) {
                return definition;
            }

            return { ...definition, ...defaultMatch };
        });

        if (normalizedDefinitions.some((definition, index) => definition !== group.definitions[index])) {
            changeDefinitions(normalizedDefinitions);
        }
    }, [group.definitions, matchTargetAttributes]);

    useEffect(() => {
        if (mappedHeaderCheckboxRef.current) {
            mappedHeaderCheckboxRef.current.indeterminate = someMappableRowsMapped;
        }
    }, [someMappableRowsMapped]);

    return (
        <div className={`external-attributes-source-row${hasValidationIssue ? ' has-error' : ''}`}>
            <div className="external-attributes-source-summary">
                <button
                    type="button"
                    className="external-attributes-expand-button"
                    title={expanded ? InovxLoader.t('facility:externalAttributes.collapse') : InovxLoader.t('facility:externalAttributes.expand')}
                    onClick={() => onToggleExpanded(link.ID)}
                >
                    <i className={`fa ${expanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                </button>
                <div className="external-attributes-source-title">
                    <div className={`external-attributes-source-name${hasDisplayNameError ? ' has-error' : ''}`} title={sourceTitle}>{sourceTitle}</div>
                    <div className="external-attributes-source-subtitle" title={sourceSubtitle}>{sourceSubtitle}</div>
                </div>
                <div className="external-attributes-source-actions">
                    {group.draftOnly && (
                        <span className="external-attributes-draft-badge">
                            {InovxLoader.t('facility:externalAttributes.draftSource')}
                        </span>
                    )}
                    {hasDrift && (
                        <span
                            className="external-attributes-drift-badge drift-1"
                            title={InovxLoader.t('facility:externalAttributes.driftSaveWarning')}
                        >
                            <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                            {InovxLoader.t('facility:externalAttributes.driftStatus')}
                        </span>
                    )}
                    <span className="external-attributes-count-badge">
                        {InovxLoader.t('facility:externalAttributes.attributeCount').replace('__count__', String(group.definitions.length))}
                    </span>
                    {mappingCountResult?.Signature === mappingSignature && (
                        <span
                            className="external-attributes-identity-count-badge"
                            title={InovxLoader.t('facility:externalAttributes.mappedAssetIdentityCountDescription')
                                .replace('__count__', String(mappingCountResult.Count))}
                        >
                            <i className="fa fa-link" aria-hidden="true" />
                            {InovxLoader.t('facility:externalAttributes.mappedAssetIdentityCount')
                                .replace('__count__', String(mappingCountResult.Count))}
                        </span>
                    )}
                    <button
                        type="button"
                        className="external-attributes-mapping-count-button"
                        title={InovxLoader.t('facility:externalAttributes.countMappedAssetIdentities')}
                        disabled={!canCountMappedAssetIdentities || mappingCountLoading}
                        onClick={countMappedAssetIdentities}
                    >
                        <i className={`fa ${mappingCountLoading ? 'fa-spinner fa-spin' : 'fa-link'}`} />
                    </button>
                    <button
                        type="button"
                        className="external-attributes-remove-button"
                        title={InovxLoader.t('facility:externalAttributes.unlink')}
                        onClick={() => onRemove(link.ID)}
                    >
                        <i className="fa fa-trash" />
                    </button>
                </div>
            </div>
            {expanded && (
                <div className="external-attributes-source-details">
                    <div className="external-attributes-detail-grid">
                        <div className="external-attributes-detail-display-name">
                            <span>{InovxLoader.t('facility:externalAttributes.linkDisplayName')}</span>
                            <input
                                className={`form-control${duplicateDisplayName || !link.DisplayName.trim() ? ' has-error' : ''}`}
                                value={link.DisplayName}
                                onChange={(event) => onLinkChange({ ...link, DisplayName: event.target.value })}
                            />
                        </div>
                    </div>

                    {!source && (
                        <div className="external-attributes-warning">
                            {InovxLoader.t('facility:externalAttributes.sourceMetadataMissing')}
                        </div>
                    )}

                    {(hasValidationIssue || hasDrift) && (
                        <div className="external-attributes-validation-stack">
                            {hasMissingDisplayName && <span>{InovxLoader.t('facility:externalAttributes.missingDisplayNameWarning')}</span>}
                            {duplicateDisplayName && <span>{InovxLoader.t('facility:externalAttributes.duplicateDisplayNameWarning')}</span>}
                            {hasMissingNames && <span>{InovxLoader.t('facility:externalAttributes.missingNameWarning')}</span>}
                            {hasDuplicateNames && <span>{InovxLoader.t('facility:externalAttributes.duplicateNameWarning')}</span>}
                            {hasDuplicateMappings && <span>{InovxLoader.t('facility:externalAttributes.duplicateMappingWarning')}</span>}
                            {hasMissingMatchTargets && <span>{InovxLoader.t('facility:externalAttributes.missingMatchTargetWarning')}</span>}
                            {hasDrift && <span>{InovxLoader.t('facility:externalAttributes.driftSaveWarning')}</span>}
                        </div>
                    )}

                    <div className="external-attributes-table-wrap">
                        <table className="external-attributes-table">
                            <thead>
                                <tr>
                                    <th>
                                        <input
                                            ref={mappedHeaderCheckboxRef}
                                            type="checkbox"
                                            checked={allMappableRowsMapped}
                                            disabled={!mappableRows.length}
                                            aria-label={InovxLoader.t('facility:externalAttributes.mapped')}
                                            onChange={toggleAllColumnsMapped}
                                        />
                                    </th>
                                    <th>{InovxLoader.t('facility:externalAttributes.attributeName')}</th>
                                    <th>{InovxLoader.t('facility:externalAttributes.normalizedDataType')}</th>
                                    <th>{InovxLoader.t('facility:externalAttributes.displayName')}</th>
                                    <th>{InovxLoader.t('facility:externalAttributes.assetKey')}</th>
                                    <th>{InovxLoader.t('facility:externalAttributes.matchTarget')}</th>
                                    <th aria-label={InovxLoader.t('facility:externalAttributes.driftStatus')} />
                                </tr>
                            </thead>
                            <tbody>
                                {!columnRows.length && (
                                    <tr>
                                        <td colSpan={7} className="external-attributes-empty-row">
                                            {InovxLoader.t('facility:externalAttributes.noColumns')}
                                        </td>
                                    </tr>
                                )}
                                {columnRows.map((row, index) => {
                                    const definition = row.definition;
                                    const mapped = !!definition;
                                    const rowKey = definition ? getRowKey(definition, index) : `${link.ID}:${row.sourceColumnName}`;
                                    const duplicateName = !!definition && duplicateNameIds.has(rowKey);
                                    const duplicateMapping = !!definition && duplicateMappingIds.has(rowKey);
                                    const matchTargetDataType = getMatchTargetDataType(row.dataType);
                                    const compatibleAttributeTargets = matchTargetAttributes.filter((attribute) => attribute.DataType === matchTargetDataType);
                                    const canUseAssetName = matchTargetDataType === ATTRIBUTE_DATA_TYPE.String;
                                    const matchTargetOptions = [
                                        ...(canUseAssetName
                                            ? [{ value: 'assetName', label: InovxLoader.t('facility:externalAttributes.assetNameTarget') }]
                                            : []),
                                        ...compatibleAttributeTargets.map((attribute) => ({
                                            value: `attribute:${attribute.ID}`,
                                            label: attribute.Name,
                                        })),
                                    ];
                                    const matchValue = definition ? getMatchValue(definition) : '';
                                    const displayedMatchValue = matchValue || matchTargetOptions[0]?.value || '';
                                    const missingMatchTarget = !!definition && !hasValidMatchTarget(definition, matchTargetAttributes);

                                    return (
                                        <tr key={rowKey} className={definition?.DriftStatus !== undefined && definition.DriftStatus !== EXTERNAL_ATTRIBUTE_DRIFT_STATUS.None ? 'has-drift' : undefined}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={mapped}
                                                    disabled={row.missingColumn && !mapped}
                                                    aria-label={InovxLoader.t('facility:externalAttributes.mapped')}
                                                    onChange={(event) => toggleColumnMapped(row.sourceColumnName, row.dataType, event.target.checked)}
                                                />
                                            </td>
                                            <td>
                                                <span className={duplicateMapping ? 'external-attributes-cell-error' : undefined} title={row.sourceColumnName}>
                                                    {row.sourceColumnName}
                                                </span>
                                            </td>
                                            <td>{getDataTypeLabel(row.dataType)}</td>
                                            <td>
                                                <input
                                                    className={`form-control${duplicateName || (definition && !definition.Name.trim()) ? ' has-error' : ''}`}
                                                    value={definition?.Name || row.sourceColumnName}
                                                    disabled={!definition}
                                                    onChange={(event) => definition && updateDefinition(definition, { Name: event.target.value })}
                                                />
                                            </td>
                                            <td>
                                                <label className="external-attributes-key-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={definition?.IsForeignKey || false}
                                                        disabled={!definition}
                                                        onChange={(event) => {
                                                            const checked = event.target.checked;
                                                            if (!definition) {
                                                                return;
                                                            }

                                                            updateDefinition(definition, {
                                                                IsForeignKey: checked,
                                                                ...(checked
                                                                    ? buildDefaultMatch(definition, matchTargetAttributes)
                                                                    : { AssetMatchTargetType: null, AssetMatchAttributeDefinitionID: null }),
                                                            });
                                                        }}
                                                    />
                                                    <span>{InovxLoader.t('facility:externalAttributes.key')}</span>
                                                </label>
                                            </td>
                                            <td>
                                                {definition?.IsForeignKey && (
                                                    <select
                                                        className={`form-control${missingMatchTarget ? ' has-error' : ''}`}
                                                        value={displayedMatchValue}
                                                        onChange={(event) => updateDefinition(definition, applyMatchTargetValue(definition, event.target.value))}
                                                    >
                                                        {matchTargetOptions.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </td>
                                            <td>
                                                {definition && definition.DriftStatus !== EXTERNAL_ATTRIBUTE_DRIFT_STATUS.None && (
                                                    <span className={`external-attributes-drift-badge drift-${definition.DriftStatus}`}>
                                                        {getDriftLabel(definition.DriftStatus)}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LinkedExternalSourceList;
